import React, { useState, useEffect } from "react";
import { CryptoAsset, PersonaRecommendations, InvestorPersona } from "../types";

interface CryptoRecommendationMatrixProps {
  assets: CryptoAsset[];
  personas: InvestorPersona[];
  recommendations: Record<string, PersonaRecommendations>;
  loading: boolean;
  expandedRowsLoading: Set<string>;
  generatedAssets: Set<string>;
  onRowExpand: (assetSymbol: string) => void;
}

const CryptoRecommendationMatrix: React.FC<CryptoRecommendationMatrixProps> = ({
  assets,
  personas,
  recommendations,
  loading,
  expandedRowsLoading,
  generatedAssets,
  onRowExpand,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [newlyExpandedAsset, setNewlyExpandedAsset] = useState<string | null>(null);

  const toggleRowExpansion = (assetSymbol: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assetSymbol)) {
        newSet.delete(assetSymbol);
      } else {
        newSet.add(assetSymbol);
        // Mark this asset as newly expanded to trigger data loading
        setNewlyExpandedAsset(assetSymbol);
      }
      return newSet;
    });
  };

  // Handle data loading for newly expanded assets
  useEffect(() => {
    if (newlyExpandedAsset) {
      onRowExpand(newlyExpandedAsset);
      setNewlyExpandedAsset(null);
    }
  }, [newlyExpandedAsset, onRowExpand]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: price < 1 ? 4 : 2,
    }).format(price);
  };

  const format24hChange = (value: number) => {
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) {
      return `$${(volume / 1e9).toFixed(2)}B`;
    } else if (volume >= 1e6) {
      return `$${(volume / 1e6).toFixed(2)}M`;
    } else if (volume >= 1e3) {
      return `$${(volume / 1e3).toFixed(2)}K`;
    } else {
      return `$${volume.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    }
  };

  const formatMarketCap = (marketCap: number) => {
    if (marketCap >= 1e12) {
      return `$${(marketCap / 1e12).toFixed(2)}T`;
    } else if (marketCap >= 1e9) {
      return `$${(marketCap / 1e9).toFixed(2)}B`;
    } else if (marketCap >= 1e6) {
      return `$${(marketCap / 1e6).toFixed(2)}M`;
    } else if (marketCap >= 1e3) {
      return `$${(marketCap / 1e3).toFixed(2)}K`;
    } else {
      return `$${marketCap.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    }
  };

  const getRecommendationCount = (assetSymbol: string) => {
    const assetRecommendations = personas
      .map((persona) => {
        const personaRecs = recommendations[persona.id];
        return personaRecs?.recommendations.find(
          (rec) => rec.asset.symbol === assetSymbol
        );
      })
      .filter(Boolean);

    return assetRecommendations.length;
  };


  const getPersonaRecommendations = (assetSymbol: string) => {
    return personas
      .map((persona) => {
        const personaRecs = recommendations[persona.id];
        const assetRec = personaRecs?.recommendations.find(
          (rec) => rec.asset.symbol === assetSymbol
        );
        return assetRec ? { persona, recommendation: assetRec } : null;
      })
      .filter(Boolean);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "BUY":
        return "bg-blue-100 text-blue-800";
      case "SELL":
        return "bg-red-100 text-red-700";
      case "HOLD":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getVolatilityRiskColor = (volRiskScore: 'Low' | 'Medium' | 'High') => {
    switch (volRiskScore) {
      case "Low":
        return "bg-green-100 text-green-800";
      case "Medium":
        return "bg-yellow-100 text-yellow-800";
      case "High":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const formatVolatility = (volatility: number) => {
    return `${(volatility * 100).toFixed(1)}%`;
  };

  const SkeletonPersonaCard = React.memo(({ persona }: { persona: InvestorPersona }) => (
    <div className="bg-white rounded-lg p-3 border border-gray-200 animate-pulse">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-gray-800">
            {persona.name}
          </p>
          <p className="text-xs text-gray-600">
            {persona.riskTolerance} • {persona.timeHorizon}
          </p>
        </div>
        <div className="h-6 bg-gray-200 rounded-full w-12"></div>
      </div>
      <div className="mb-3">
        <div className="h-3 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="bg-gray-50 rounded-lg p-2 mb-2">
        <div className="flex items-center justify-between mb-2">
          <div className="h-3 bg-gray-200 rounded w-20"></div>
          <div className="h-5 bg-gray-200 rounded-full w-16"></div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="col-span-2 h-3 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded w-full"></div>
        <div className="h-3 bg-gray-200 rounded w-4/5"></div>
        <div className="h-3 bg-gray-200 rounded w-3/5"></div>
      </div>
    </div>
  ));

  const SkeletonPersonaCardDesktop = React.memo(({ persona }: { persona: InvestorPersona }) => (
    <div className="bg-white rounded-lg p-4 border border-gray-300 animate-pulse">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h5 className="text-sm font-medium text-gray-800">
            {persona.name}
          </h5>
          <p className="text-xs text-gray-600">
            {persona.riskTolerance} Risk • {persona.timeHorizon} Term
          </p>
        </div>
        <div className="h-8 bg-gray-200 rounded-full w-16"></div>
      </div>
      <div className="space-y-2 mb-3">
        <div className="flex justify-between">
          <div className="h-3 bg-gray-200 rounded w-16"></div>
          <div className="h-3 bg-gray-200 rounded w-8"></div>
        </div>
        <div className="flex justify-between">
          <div className="h-3 bg-gray-200 rounded w-14"></div>
          <div className="h-3 bg-gray-200 rounded w-12"></div>
        </div>
      </div>
      <div className="pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <div className="h-3 bg-gray-200 rounded w-24"></div>
          <div className="h-5 bg-gray-200 rounded-full w-16"></div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="flex justify-between">
            <div className="h-3 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-8"></div>
          </div>
          <div className="flex justify-between">
            <div className="h-3 bg-gray-200 rounded w-12"></div>
            <div className="h-3 bg-gray-200 rounded w-8"></div>
          </div>
          <div className="col-span-2 flex justify-between">
            <div className="h-3 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-20"></div>
          </div>
        </div>
        <div className="pt-3 border-t border-gray-100">
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            <div className="h-3 bg-gray-200 rounded w-3/5"></div>
          </div>
        </div>
      </div>
    </div>
  ));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Generating ChatGPT recommendations for all personas...
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mobile Card Layout (sm and below) */}
      <div className="block lg:hidden space-y-4">
        {assets.map((asset, index) => {
          const recommendationCount = getRecommendationCount(asset.symbol);

          return (
            <div
              key={asset.symbol}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="p-4">
                {/* Asset Header */}
                <div className="flex items-center mb-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm font-medium text-gray-600">
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {asset.name}
                    </h3>
                    <p className="text-sm text-gray-500">{asset.symbol}</p>
                  </div>
                </div>

                {/* Price Information */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Current Price
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatPrice(asset.currentPrice)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      24h Change
                    </p>
                    <p className="text-sm font-semibold">
                      {asset.priceChange24h != null ? (
                        <span
                          className={
                            asset.priceChange24h >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {format24hChange(asset.priceChange24h)}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Volume and Market Cap Information */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      24h Volume
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {asset.volume24h ? (
                        formatVolume(asset.volume24h)
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Market Cap
                    </p>
                    <p className="text-sm font-semibold text-gray-900">
                      {asset.marketCap ? (
                        formatMarketCap(asset.marketCap)
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Personas */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {generatedAssets.has(asset.symbol) ? (
                      <span className="text-sm text-gray-700">
                        {recommendationCount} persona{recommendationCount !== 1 ? "s" : ""}
                      </span>
                    ) : (
                      <button
                        onClick={() => toggleRowExpansion(asset.symbol)}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                        title="Click to generate AI recommendations for this asset"
                      >
                        <span className="mr-1">🤖</span>
                        Generate Analysis
                      </button>
                    )}
                    {generatedAssets.has(asset.symbol) && (
                      <button
                        onClick={() => toggleRowExpansion(asset.symbol)}
                        className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <span
                          className={`inline-block transition-transform ${
                            expandedRows.has(asset.symbol) ? "rotate-180" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Mobile Persona Details */}
              {expandedRows.has(asset.symbol) && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-800 mb-3">
                    Persona Recommendations
                  </h4>
                  {expandedRowsLoading.has(asset.symbol) ? (
                    <div className="space-y-3">
                      {personas.map((persona, index) => (
                        <SkeletonPersonaCard key={persona.id} persona={persona} />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getPersonaRecommendations(asset.symbol).map(
                        ({ persona, recommendation }: any) => (
                        <div
                          key={persona.id}
                          className="bg-white rounded-lg p-3 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {persona.name}
                              </p>
                              <p className="text-xs text-gray-600">
                                {persona.riskTolerance} • {persona.timeHorizon}
                              </p>
                            </div>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActionColor(
                                recommendation.action
                              )}`}
                            >
                              {recommendation.action}
                            </span>
                          </div>
                          <div className="mb-3">
                            <div className="text-xs">
                              <span className="text-gray-600">Confidence:</span>
                              <span className="ml-1 font-medium text-gray-800">
                                {(recommendation.confidence * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Volatility Analysis Section */}
                          {true && (
                            <div className="bg-gray-50 rounded-lg p-2 mb-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-700">Volatility Analysis</span>
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getVolatilityRiskColor(
                                    (recommendation.volatilityAnalysis?.volRiskScore || "Medium") as any
                                  )}`}
                                >
                                  {recommendation.volatilityAnalysis?.volRiskScore || "Medium"} Risk
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-600">Historical (30d):</span>
                                  <span className="ml-1 font-medium text-gray-800">
                                    {formatVolatility(recommendation.volatilityAnalysis?.historicalVol30d || 0.5)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Implied:</span>
                                  <span className="ml-1 font-medium text-gray-800">
                                    {formatVolatility(recommendation.volatilityAnalysis?.impliedVol || 0.6)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-gray-600">3mo Range:</span>
                                  <span className="ml-1 font-medium text-gray-800">
                                    {formatPrice(recommendation.volatilityAnalysis?.blackScholesRange?.lower || recommendation.asset.currentPrice * 0.8)} - {formatPrice(recommendation.volatilityAnalysis?.blackScholesRange?.upper || recommendation.asset.currentPrice * 1.2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-600 leading-relaxed">
                            {recommendation.reasoning}
                          </p>
                        </div>
                      )
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop Table Layout (lg and above) */}
      <div className="hidden lg:block rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="border-b-2 border-gray-300">
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Price
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  24h Change
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  24h Volume
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Market Cap
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Personas
                </th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset, index) => {
                const recommendationCount = getRecommendationCount(asset.symbol);

                return (
                  <React.Fragment key={asset.symbol}>
                    <tr className="hover:bg-gray-50 transition-colors border-b border-gray-200">
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-gray-900">
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-base font-medium text-gray-900">
                            {asset.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {asset.symbol}
                          </div>
                        </div>
                      </td>


                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                        {formatPrice(asset.currentPrice)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {asset.priceChange24h != null ? (
                          <span
                            className={`text-sm font-semibold ${
                              asset.priceChange24h >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {format24hChange(asset.priceChange24h)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                        {asset.volume24h ? (
                          formatVolume(asset.volume24h)
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                        {asset.marketCap ? (
                          formatMarketCap(asset.marketCap)
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {generatedAssets.has(asset.symbol) ? (
                            <span className="text-sm font-normal text-gray-900">
                              {recommendationCount} rating{recommendationCount !== 1 ? "s" : ""}
                            </span>
                          ) : (
                            <button
                              onClick={() => toggleRowExpansion(asset.symbol)}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                              title="Click to generate AI recommendations for this asset"
                            >
                              <span className="mr-1">🤖</span>
                              Generate Analysis
                            </button>
                          )}
                          {generatedAssets.has(asset.symbol) && (
                            <button
                              onClick={() => toggleRowExpansion(asset.symbol)}
                              className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              <span
                                className={`inline-block transition-transform ${
                                  expandedRows.has(asset.symbol)
                                    ? "rotate-180"
                                    : ""
                                }`}
                              >
                                ▼
                              </span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row for persona breakdown */}
                    {expandedRows.has(asset.symbol) && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="space-y-3">
                            <h4 className="text-sm font-medium text-gray-800 mb-3">
                              Persona Recommendations for {asset.symbol}
                            </h4>
                            {expandedRowsLoading.has(asset.symbol) ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {personas.map((persona) => (
                                  <SkeletonPersonaCardDesktop key={persona.id} persona={persona} />
                                ))}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {getPersonaRecommendations(asset.symbol).map(
                                  ({ persona, recommendation }: any) => (
                                  <div
                                    key={persona.id}
                                    className="bg-white rounded-lg p-4 border border-gray-300"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div>
                                        <h5 className="text-sm font-medium text-gray-800">
                                          {persona.name}
                                        </h5>
                                        <p className="text-xs text-gray-600">
                                          {persona.riskTolerance} Risk •{" "}
                                          {persona.timeHorizon} Term
                                        </p>
                                      </div>
                                      <span
                                        className={`inline-flex px-3 py-2 text-sm font-medium rounded-full ${getActionColor(
                                          recommendation.action
                                        )}`}
                                      >
                                        {recommendation.action}
                                      </span>
                                    </div>

                                    <div className="space-y-2">
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">
                                          Confidence:
                                        </span>
                                        <span className="font-medium text-gray-800">
                                          {(
                                            recommendation.confidence * 100
                                          ).toFixed(0)}
                                          %
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">
                                          Timeframe:
                                        </span>
                                        <span className="font-medium text-gray-800">
                                          {recommendation.timeframe}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Volatility Analysis Section */}
                                    {true && (
                                      <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-xs font-medium text-gray-700">Volatility Analysis</span>
                                          <span
                                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getVolatilityRiskColor(
                                              (recommendation.volatilityAnalysis?.volRiskScore || "Medium") as any
                                            )}`}
                                          >
                                            {recommendation.volatilityAnalysis?.volRiskScore || "Medium"}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Historical (30d):</span>
                                            <span className="font-medium text-gray-800">
                                              {formatVolatility(recommendation.volatilityAnalysis?.historicalVol30d || 0.5)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Implied:</span>
                                            <span className="font-medium text-gray-800">
                                              {formatVolatility(recommendation.volatilityAnalysis?.impliedVol || 0.6)}
                                            </span>
                                          </div>
                                          <div className="col-span-2 flex justify-between">
                                            <span className="text-gray-600">3mo Range:</span>
                                            <span className="font-medium text-gray-800">
                                              {formatPrice(recommendation.volatilityAnalysis?.blackScholesRange?.lower || recommendation.asset.currentPrice * 0.8)} - {formatPrice(recommendation.volatilityAnalysis?.blackScholesRange?.upper || recommendation.asset.currentPrice * 1.2)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                      <p className="text-xs text-gray-600 leading-relaxed">
                                        {recommendation.reasoning}
                                      </p>
                                    </div>
                                  </div>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">
        <div>
          <span className="font-medium">24h Change:</span> 24-hour price change percentage
        </div>
        <div>
          <span className="font-medium">24h Volume:</span> 24-hour trading volume in USD
        </div>
        <div>
          <span className="font-medium">Market Cap:</span> Total market capitalization in USD
        </div>
        <div>
          <span className="font-medium">Personas:</span> Number of persona
          recommendations available for this asset
        </div>
      </div>
    </div>
  );
};

export default CryptoRecommendationMatrix;
